<template>
  <content-modal
    ref="speechEvaluationModal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    :primary-button-disabled="false"
    class="speech-evaluation-modal"
    @primary-click="embed"
    @modal-hide-request="actionHideRequest"
  >
    <template #title>{{ t("speech_evaluation_add_title") }}</template>
    <template>{{ instructions }}</template>
    <template #content>
      <modal-content>
        <span class="info">{{ t("speech_evaluation_add_subtitle") }}</span>
        <cv-form>
          <text-area
            ref="speechEvaluationTextInput"
            v-model:value="speechEvaluationText"
            :disabled="false"
            :character-limit="characterLimit"
            :error-message="speechEvaluationTextErrorMessage"
            :mandatory="true"
            :custom-validation="true"
            @input="handleUpdateSpeechEvaluationText"
          />
        </cv-form>
        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="actionHideRequest">
            {{ t("button_cancel") }}
          </cv-button>
          <cv-button class="save" kind="primary" size="field" @click="embed">
            {{ t("button_save") }}
          </cv-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import modalEventBus from "@plugins/modal/service/event-bus";
import randomUtils from "@utils/random.utils";
import i18n from "@plugins/i18n";
import { SpeechEvaluationEventBus } from "@plugins/richtext/plugins/speech-evaluation/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import TextArea from "@plugins/common/components/TextArea.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";

const props = defineProps<{
  params: {
    initialContent?: string;
    eventBusWatchInstances?: unknown;
  };
}>();

const { t } = useI18n();
const instance = getCurrentInstance();

const id = randomUtils.randomId();
const dirtyId = ref(id);
const { setDirty, removeDirty, clearDirty, promptComponentDirty } = useDirty(dirtyId);

const speechEvaluationText = ref<string>(props.params.initialContent ?? "");
const autoHideOff = ref(true);
const speechEvaluationTextErrorMessage = ref("");
const isLoading = ref(false);
const size = ref("large");
const closeAriaLabel = ref("Close");
const visible = ref(true);
const characterLimit = ref(2000);
const sentenceCharacterLimit = ref(100);

const speechEvaluationTextInput = useTemplateRef<InstanceType<typeof TextArea>>(
  "speechEvaluationTextInput",
);

const isValid = computed(() => validateSpeechEvaluationText());
const instructions = computed(() =>
  t("speech_evaluation_create_instruction", { limit: sentenceCharacterLimit.value }),
);

function actionHideRequest(event: Record<string, string>) {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    promptComponentDirty().then((response) => {
      if (!response) {
        return;
      }
      visible.value = false;
      EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
      modalEventBus.$emit("modal-hidden", instance?.vnode.key);
    });
  }
}

function validateSpeechEvaluationText() {
  if (!speechEvaluationTextInput.value?.validateMaxLimit()) {
    speechEvaluationTextErrorMessage.value = t("character_limit_invalid_message", {
      num: characterLimit.value,
    }) as string;
    return false;
  } else if (speechEvaluationText.value.trim().length === 0) {
    speechEvaluationTextErrorMessage.value = i18n.t("field_empty_error_message") as string;
    return false;
  } else {
    return true;
  }
}

function embed() {
  if (isValid.value) {
    isLoading.value = true;
    removeDirty(id);
    SpeechEvaluationEventBus.$emit("embed-speechEvaluation", speechEvaluationText.value);
    visible.value = false;
    EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
  }
}

function handleUpdateSpeechEvaluationText(value: string) {
  speechEvaluationText.value = value;
  speechEvaluationTextErrorMessage.value = "";
  setDirty(id);
  if (speechEvaluationText.value.length === 0) {
    clearDirty();
  }
}
</script>

<style lang="scss">
.speech-evaluation-modal {
  .bx--text-area {
    min-height: 14rem;
  }
}
</style>
